import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BrandingService,
  CardAction,
  CrmFieldService,
  CrmObjectService,
  CrmPlatformApiService,
  PlatformExtensionFieldIds,
} from '@galaxy/crm/static';
import { TranslateService } from '@ngx-translate/core';
import { AddContactAsIamUserForCompanyStatus } from '@vendasta/crm-integrations';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, User, UserIdentifier } from '@vendasta/iamv2';
import { BehaviorSubject, Observable, Subject, combineLatest, interval, map, of, retry, take } from 'rxjs';
import { filter, shareReplay, switchMap, tap } from 'rxjs/operators';
import { RemoveModalComponent } from './remove-modal/remove-modal.component';

export interface AppAccess {
  hasBusinessAppAccess$$: Subject<boolean | null>;
  hasBusinessAppAccess$: Observable<boolean | null>;
  desiredBusinessAppAccess$$: Subject<boolean | null>;
  actions$: Observable<CardAction[]>;
}

@Injectable()
export class AccessBusinessAppService {
  businessAppName$: Observable<string>;

  constructor(
    private readonly iamService: IAMService,
    private readonly objectService: CrmObjectService,
    private readonly fieldService: CrmFieldService,
    private readonly brandingService: BrandingService,
    private readonly platformService: CrmPlatformApiService,
    private readonly translateService: TranslateService,
    private readonly snackService: SnackbarService,
  ) {
    this.businessAppName$ = this.brandingService.getBusinessAppName();
  }

  addContactAsIAMUser(
    contactId: string,
    companyId: string,
    sendWelcomeEmail: boolean,
  ): Observable<AddContactAsIamUserForCompanyStatus> {
    return this.platformService.addContactAsIamUserForCompany(contactId, companyId, sendWelcomeEmail).pipe(
      map((response) => {
        if (!response) {
          return AddContactAsIamUserForCompanyStatus.ADD_CONTACT_AS_IAM_USER_FOR_COMPANY_STATUS_INVALID;
        } else if (
          response === AddContactAsIamUserForCompanyStatus.ADD_CONTACT_AS_IAM_USER_FOR_COMPANY_STATUS_SUCCEEDED
        ) {
          // success.  Stop retrying.
          return response;
        } else if (response === AddContactAsIamUserForCompanyStatus.ADD_CONTACT_AS_IAM_USER_FOR_COMPANY_STATUS_FAILED) {
          // failure.  Stop retrying.
          return response;
        }
        // throw error to poll again
        throw false;
      }),
      retry({ delay: 2000 }),
    );
  }

  removeContactAsIAMUser(contactId: string, companyId: string): Observable<boolean> {
    return this.platformService.removeContactAsIamUserForCompany(contactId, companyId);
  }

  getLastLogin(contactId: string): Observable<string> {
    const user$ = this.objectService.getMultiObject('Contact', [contactId]).pipe(
      map((resp) => {
        const object = resp?.crmObjects?.[0];
        if (resp?.crmObjects?.length === 0 || !object) {
          return '';
        }
        return this.fieldService.getFieldValueFromCrmObject(object, PlatformExtensionFieldIds.UserID)?.stringValue;
      }),
      switchMap((userId) => {
        if (!userId) {
          return of(undefined);
        }
        return this.iamService.getUser(new UserIdentifier({ userId }));
      }),
    );

    return user$.pipe(
      switchMap((user: User) => {
        const lastLogin = user?.lastLogin;
        if (lastLogin && lastLogin.getFullYear() > 0) {
          return of(lastLogin.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }));
        }
        return of(this.translateService.instant('ASSOCIATIONS.COMPANY.NEVER_LOGGED_IN'));
      }),
    );
  }

  hasAccess(contactId: string, companyId: string): Observable<boolean> {
    return this.platformService.isContactIamUserForCompany(contactId, companyId).pipe(
      map((response) => {
        return response;
      }),
      take(1),
    );
  }

  getAppAccess(dialog: MatDialog, contactId: string, companyId: string, contactName, companyName): AppAccess {
    const desiredAccess$$ = new BehaviorSubject<boolean | null>(null);
    const hasBusinessAppAccess$$ = new BehaviorSubject<boolean | null>(null);
    const hasBusinessAppAccess$ = combineLatest([
      interval(2000),
      hasBusinessAppAccess$$.asObservable(),
      desiredAccess$$.asObservable(),
    ]).pipe(
      filter(([_, hasAccess, desired]) => {
        return hasAccess === null || hasAccess !== desired;
      }),
      switchMap(() => {
        return this.hasAccess(contactId, companyId);
      }),
      tap((hasAccess) => {
        hasBusinessAppAccess$$?.next(hasAccess);
        if (desiredAccess$$.getValue() === null) {
          desiredAccess$$?.next(hasAccess);
        }
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    const actions$ = combineLatest([hasBusinessAppAccess$, this.businessAppName$]).pipe(
      map(([hasAccess, businessAppName]) => {
        if (hasAccess) {
          return [
            {
              label: this.translateService.instant(
                'CRM.ASSOCIATION_PANEL.CONTACTS.REMOVE_CONTACT_AS_BUSINESS_APP_USER.ACTION',
                { businessAppName: businessAppName },
              ),
              callback: () => {
                dialog
                  .open(RemoveModalComponent, {
                    width: '560px',
                    data: {
                      title: this.translateService.instant(
                        'CRM.ASSOCIATION_PANEL.CONTACTS.REMOVE_CONTACT_AS_BUSINESS_APP_USER.DIALOG_TITLE',
                        { businessAppName: businessAppName },
                      ),
                      content: this.translateService.instant(
                        'CRM.ASSOCIATION_PANEL.CONTACTS.REMOVE_CONTACT_AS_BUSINESS_APP_USER.DIALOG_CONTENT',
                        { contactName: contactName, companyName: companyName },
                      ),
                    },
                  })
                  .afterClosed()
                  .subscribe((confirmed) => {
                    if (confirmed) {
                      this.removeContactAsIAMUser(contactId, companyId).subscribe({
                        next: () => {
                          const translatedString = this.translateService.instant(
                            'CRM.ASSOCIATION_PANEL.CONTACTS.REMOVE_CONTACT_AS_BUSINESS_APP_USER.SUCCESS',
                            {
                              contactName: contactName,
                              companyName: companyName,
                            },
                          );
                          this.snackService.openSuccessSnack(translatedString);
                          desiredAccess$$.next(false);
                        },
                        error: (err) => {
                          console.error(err);
                          const translatedString = this.translateService.instant(
                            'CRM.ASSOCIATION_PANEL.CONTACTS.REMOVE_CONTACT_AS_BUSINESS_APP_USER.ERROR',
                            {
                              contactName: contactName,
                              companyName: companyName,
                            },
                          );
                          this.snackService.openErrorSnack(translatedString);
                        },
                      });
                    }
                  });
              },
            },
          ];
        }
        return [];
      }),
    );
    return {
      actions$: actions$,
      desiredBusinessAppAccess$$: desiredAccess$$,
      hasBusinessAppAccess$: hasBusinessAppAccess$,
      hasBusinessAppAccess$$: hasBusinessAppAccess$$,
    };
  }
}
